import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useMutation } from '@tanstack/react-query';

import { selectAuthToken } from 'src/store/selectors';
import { removeUserFromStudy } from 'src/api/users';
import { LoadingLoop, Modal } from '@medeloop/medeloop-components-library';
import { useStudyDetailsContext } from '../drawer/study-details';
import { useTranslation } from 'react-i18next';

enum StudyAction {
  UNASSIGN_RESEARCHER = 'UNASSIGN_RESEARCHER',
}

interface StudyActionParameters {
  id: string;
  name: string;
  action: string;
}

interface StudyActionResults {
  title: string;
  question: string;
  confirm: string;
  apiCall: Function;
}

const selectParameters = ({ id, name, action }: StudyActionParameters): StudyActionResults => {
  const { t } = useTranslation();

  switch (action) {
    case 'UNASSIGN_RESEARCHER': {
      return {
        title: t('unassign_researcher'),
        question: t('unassign_researcher_from_study_confirmation', { name, id }),
        confirm: t(`unassign_researcher`),
        apiCall: removeUserFromStudy,
      };
    }

    default: {
      return {
        title: '',
        question: '',
        confirm: '',
        apiCall: () => {},
      };
    }
  }
};

export const StudyActionsPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = useSelector(selectAuthToken);

  const { container } = useStudyDetailsContext();

  const name = params.get('name') || '';
  const id = params.get('id') || '';
  const action = params.get('action') || '';
  const role = params.get('role') || '';
  const userId = params.get('userId') || '';

  // If none of the attributes are present, go back.
  if (!name || !id || !action || !(action in StudyAction)) {
    navigate(-1);
  }

  const { title, question, confirm, apiCall } = selectParameters({
    id,
    name,
    action,
  });

  const { isPending, mutate } = useMutation({
    mutationFn: () => {
      return apiCall({ token, id, studyId: id, role, userId });
    },
    onSuccess: () => {
      if (action === 'DELETE') navigate('/overview/');
      else navigate(`/overview/studies/${id}`);
    },
  });

  const handleConfirm = () => {
    mutate();
  };

  return (
    <Modal
      isOpen
      title={title}
      actionText={confirm}
      actionFunction={handleConfirm}
      actionType={action === 'DELETE' ? 'danger' : 'primary'}
      onCancel={() => navigate(-1)}
      container={container}
    >
      <div className="w-96">
        {isPending ? (
          <div className="flex flex-grow items-center justify-center">
            <LoadingLoop className="w-16" />
          </div>
        ) : (
          question
        )}
      </div>
    </Modal>
  );
};
